/**
 * 💰 Cost Breakdown Validation Utility
 *
 * Validates that cost breakdowns from the backend are mathematically correct.
 * Per API contract, frontend should NEVER recalculate values, but should validate
 * them and warn if inconsistencies are detected.
 */

export interface CostBreakdown {
  rentalCost: number;
  bookingFee: number;
  bookingFeeRate?: number;
  vat: number;
  vatRate?: number;
  securityDeposit?: number;
  totalAmount: number;
  driverFee?: number;
  deliveryFee?: number;
  hostPayout?: number;
  serviceFee?: number;
  serviceFeeRate?: number;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validates a cost breakdown for mathematical correctness
 *
 * @param breakdown - Cost breakdown object from backend
 * @param toleranceNaira - Tolerance for rounding differences (default: 1 Naira)
 * @returns Validation result with errors and warnings
 */
export function validateCostBreakdown(
  breakdown: Partial<CostBreakdown>,
  toleranceNaira: number = 1
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!breakdown) {
    errors.push('Cost breakdown is null or undefined');
    return { isValid: false, errors, warnings };
  }

  // Validation 1: Booking fee rate should be around 5%, not 0.1%
  if (breakdown.bookingFeeRate !== undefined) {
    if (breakdown.bookingFeeRate < 1) {
      errors.push(
        `Booking fee rate is ${breakdown.bookingFeeRate}% - should be around 5%. This suggests 0.1 was used instead of 5.`
      );
    } else if (breakdown.bookingFeeRate > 10) {
      warnings.push(
        `Booking fee rate is ${breakdown.bookingFeeRate}% - unusually high (expected ~5%)`
      );
    }
  }

  // Validation 2: Booking fee should match booking fee rate × rental cost
  if (breakdown.rentalCost && breakdown.bookingFee) {
    const expectedRate = breakdown.bookingFeeRate || 5;
    const calculatedFee = Math.round(breakdown.rentalCost * (expectedRate / 100) * 100) / 100;
    const difference = Math.abs(breakdown.bookingFee - calculatedFee);

    if (difference > toleranceNaira) {
      errors.push(
        `Booking fee mismatch: Got ₦${breakdown.bookingFee}, expected ₦${calculatedFee} ` +
        `(${expectedRate}% of ₦${breakdown.rentalCost}). Difference: ₦${difference.toFixed(2)}`
      );
    }
  }

  // Validation 3: VAT should be ~7.5% of (rental cost + booking fee)
  if (breakdown.rentalCost && breakdown.bookingFee && breakdown.vat !== undefined) {
    const vatBase = breakdown.rentalCost + breakdown.bookingFee;
    const expectedVatRate = breakdown.vatRate || 7.5;
    const calculatedVat = Math.round(vatBase * (expectedVatRate / 100) * 100) / 100;
    const difference = Math.abs(breakdown.vat - calculatedVat);

    if (difference > toleranceNaira) {
      errors.push(
        `VAT mismatch: Got ₦${breakdown.vat}, expected ₦${calculatedVat} ` +
        `(${expectedVatRate}% of ₦${vatBase}). Difference: ₦${difference.toFixed(2)}`
      );
    }
  }

  // Validation 4: Total should sum correctly
  if (breakdown.totalAmount !== undefined && breakdown.rentalCost && breakdown.bookingFee) {
    const expectedTotal =
      breakdown.rentalCost +
      breakdown.bookingFee +
      (breakdown.vat || 0) +
      (breakdown.securityDeposit || 0) +
      (breakdown.driverFee || 0) +
      (breakdown.deliveryFee || 0);

    const difference = Math.abs(breakdown.totalAmount - expectedTotal);

    if (difference > toleranceNaira) {
      errors.push(
        `Total amount mismatch: Got ₦${breakdown.totalAmount}, expected ₦${expectedTotal.toFixed(2)}. ` +
        `Difference: ₦${difference.toFixed(2)}`
      );
    }
  }

  // Validation 5: Host payout should be rental cost - service fee
  if (
    breakdown.hostPayout !== undefined &&
    breakdown.rentalCost &&
    breakdown.serviceFee !== undefined
  ) {
    const expectedPayout = breakdown.rentalCost - breakdown.serviceFee;
    const difference = Math.abs(breakdown.hostPayout - expectedPayout);

    if (difference > toleranceNaira) {
      warnings.push(
        `Host payout mismatch: Got ₦${breakdown.hostPayout}, expected ₦${expectedPayout.toFixed(2)}. ` +
        `Difference: ₦${difference.toFixed(2)}`
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Quick check if a breakdown appears to use the old 0.1% bug
 */
export function hasOldBookingFeeBug(breakdown: Partial<CostBreakdown>): boolean {
  if (!breakdown) return false;

  // Check if booking fee rate is less than 1% (likely 0.1 instead of 5)
  if (breakdown.bookingFeeRate !== undefined && breakdown.bookingFeeRate < 1) {
    return true;
  }

  // Check if booking fee is way too low compared to rental cost
  if (breakdown.rentalCost && breakdown.bookingFee) {
    const actualRate = (breakdown.bookingFee / breakdown.rentalCost) * 100;
    if (actualRate < 1) {
      // Booking fee is less than 1% of rental cost - definitely wrong
      return true;
    }
  }

  return false;
}

/**
 * Format validation errors for display to user
 */
export function formatValidationErrors(result: ValidationResult): string {
  if (result.isValid) {
    return '';
  }

  const allMessages = [...result.errors, ...result.warnings];
  return allMessages.join('\n');
}

/**
 * Log validation errors to console with helpful formatting
 */
export function logValidationErrors(
  result: ValidationResult,
  breakdown: Partial<CostBreakdown>,
  bookingId?: string
): void {
  if (result.isValid) {
    return;
  }

  console.group(`⚠️ Cost Breakdown Validation Failed${bookingId ? ` - Booking ${bookingId}` : ''}`);

  console.log('Breakdown Data:', breakdown);

  if (result.errors.length > 0) {
    console.error('❌ Errors:');
    result.errors.forEach((error, index) => {
      console.error(`  ${index + 1}. ${error}`);
    });
  }

  if (result.warnings.length > 0) {
    console.warn('⚠️  Warnings:');
    result.warnings.forEach((warning, index) => {
      console.warn(`  ${index + 1}. ${warning}`);
    });
  }

  console.log(
    '💡 Suggestion: This booking may have been created with an old/buggy backend calculation. ' +
    'Contact support to recalculate this booking.'
  );

  console.groupEnd();
}
